import React, { useState, useEffect, useMemo, useCallback } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import axios from 'axios';
import MainLayout from '../../../layout/main-layout';
import { APP_API_URL, APP_NAME } from '../../../utils';
import formatNumber, {
  defaultFilterDates,
  datesForPeriod,
  formatDate,
  DUO_PERIOD_OPTIONS,
  isDateInRange,
} from '../../../utils/functions';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import DuoModal from '../../../components/ui/duo-modal';
import DuoPeriodPicker from '../../../components/ui/duo-period-picker';
import BrokenTileInfoHint from '../../../components/ui/broken-tile-info-hint';
import {
  getSaleNetRevenue,
  isBrokenTileSaleValidated,
  TOOLTIP_COST_FIFO_PERIOD,
  TOOLTIP_COST_FIFO_SALE,
  TOOLTIP_ENCAISSEMENTS,
  TOOLTIP_LOSS,
  TOOLTIP_LOSS_PERIOD,
  TOOLTIP_M2_FIFO,
} from '../../../utils/broken-tile-sale';

const PERIOD_THIS_YEAR = { label: 'Cette année', value: 'this_year' };
const PERIOD_CUSTOM = { label: '', value: '' };
const periodOptions = [...DUO_PERIOD_OPTIONS, PERIOD_THIS_YEAR];

const DEFAULT_FILTERS = {
  search: '',
  period: '',
  ...defaultFilterDates(),
  validation: '',
};

const dateInputStart = (dateStr) => {
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return d;
};

const dateInputEnd = (dateStr) => {
  const d = new Date(dateStr);
  d.setHours(23, 59, 59, 999);
  return d;
};

const PER_PAGE = 10;

const PAYMENT_METHODS = {
  1: 'Mobile Money',
  2: 'Carte bancaire',
  3: 'Virement',
  4: 'Espèces',
  5: 'Crédit',
  6: 'Autre',
  7: 'Chèques',
};

const getPaymentLabel = (value) => PAYMENT_METHODS[value] || PAYMENT_METHODS[String(value)] || value;

const isSaleValidated = isBrokenTileSaleValidated;

const ValidationBadge = ({ validated }) =>
  validated ? (
    <span className="duo-badge-pill duo-badge-pill--ok">Validée</span>
  ) : (
    <span className="duo-badge-pill duo-badge-pill--warn">Non validée</span>
  );

const normalizeItems = (items) => {
  if (Array.isArray(items)) return items;
  if (typeof items === 'string') {
    try {
      return JSON.parse(items);
    } catch {
      return [];
    }
  }
  return [];
};

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const getSortValue = (item, key) => {
  if (key === 'customer') return item.customer?.name ?? '';
  if (key === 'user') return item.user?.name ?? '';
  if (key === 'items') return normalizeItems(item.items).length;
  if (key === 'total_amount') return getSaleNetRevenue(item);
  if (key === 'discount') return parseFloat(item.discount) || 0;
  if (key === 'payment_method') return getPaymentLabel(item.payment_method);
  if (key === 'note') return item.note ?? '';
  if (key === 'created_at') return item.created_at ?? '';
  if (key === 'validated') return isSaleValidated(item) ? 1 : 0;
  return item[key] ?? '';
};

const itemM2 = (item) => {
  if (item?.m2 != null) return parseFloat(item.m2) || 0;
  const bags = parseFloat(item?.quantity) || 0;
  const bagSize = parseFloat(item?.format ?? item?.bag_size ?? item?.broken_tile_id) || 0;
  return bags * bagSize;
};

const LossValue = ({ value, suffix = ' XOF' }) => {
  const n = parseFloat(value) || 0;
  const isLoss = n > 0;
  const isGain = n < 0;
  return (
    <span style={{ color: isLoss ? '#c0392b' : isGain ? '#27ae60' : undefined }}>
      {isLoss ? '+' : ''}
      {formatNumber(Math.abs(n))}
      {suffix}
      {isLoss ? ' (perte)' : isGain ? ' (marge)' : ''}
    </span>
  );
};

const CALCULATION_NOTE = {
  title: 'Note — méthode de calcul',
  sections: [
    {
      heading: 'Surfaces (m²)',
      items: [
        'm² cassés : somme des quantités enregistrées dans « Carreaux cassés » sur la période filtrée.',
        'm² vendus : pour chaque vente, nombre de sacs × format du sac (1, 1,5, 1,75 ou 2 m²). Les ventes hors période ne comptent pas dans les KPI période, mais alimentent le stock FIFO.',
        'm² restants : stock cassé encore disponible après toutes les ventes (toutes périodes confondues, jusqu’à la date de fin du filtre).',
      ],
    },
    {
      heading: 'Coût de revient',
      items: [
        'Chaque entrée « carreau cassé » conserve un prix de revient au m² (snapshot à l’enregistrement, issu de l’approvisionnement).',
        'Lors d’une vente, le coût est calculé en FIFO : on consomme d’abord les entrées les plus anciennes, chaque m² vendu emporte le coût de revient du lot correspondant.',
        'Coût revient vendu (période) = somme des coûts FIFO des m² vendus sur la période.',
      ],
    },
    {
      heading: 'Chiffre d’affaires et perte',
      items: [
        'CA vente = montant net encaissé (après remise), ou montant net TTC si disponible.',
        'Perte nette = coût revient vendu − CA. Un montant positif indique une perte (on revend en dessous du coût) ; un montant négatif indique une marge.',
        'Par vente : perte = coût FIFO de la facture − CA net de la facture.',
      ],
    },
    {
      heading: 'Répartition par carreau (estimation)',
      items: [
        'À la vente, seul le format du sac (m²) est saisi : le carreau précis n’est pas choisi.',
        'La répartition par carreau est donc estimée via le même FIFO : les m² vendus sont affectés aux variantes dans l’ordre d’enregistrement des cassures.',
        'CA alloué par carreau = part du CA de la vente proportionnelle aux m² FIFO imputés à ce carreau (remise répartie au prorata).',
        'Perte par carreau = coût vendu alloué − CA alloué.',
      ],
    },
  ],
};

const CalculationNotePanel = () => {
  const [open, setOpen] = useState(false);

  return (
    <section className="duo-panel mt-3 mb-0">
      <button
        type="button"
        className="duo-toolbar w-100 border-0 bg-transparent text-start"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        style={{ cursor: 'pointer', borderBottom: open ? undefined : 'none' }}
      >
        <span className="fw-bold" style={{ color: '#6b7280', fontSize: '0.85rem' }}>
          <i className="bi bi-journal-text me-1" />
          {CALCULATION_NOTE.title}
        </span>
        <span className="small text-muted d-inline-flex align-items-center gap-1">
          {open ? 'Masquer' : 'Afficher'}
          <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
        </span>
      </button>
      {open && (
        <div
          className="px-3 pb-3 border-top"
          style={{ fontSize: '0.82rem', lineHeight: 1.5, color: '#555', borderColor: '#f0f0f0' }}
        >
          {CALCULATION_NOTE.sections.map((section) => (
            <div key={section.heading} className="mb-3 mt-3">
              <p className="fw-bold mb-1" style={{ color: '#3c3c3c', fontSize: '0.84rem' }}>
                {section.heading}
              </p>
              <ul className="mb-0 ps-3">
                {section.items.map((item) => (
                  <li key={item} className="mb-1">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <p className="duo-info-hint duo-info-hint--warn mb-0">
            Les chiffres « par carreau » restent une estimation tant que la vente ne permet pas de sélectionner
            explicitement la variante vendue.
          </p>
        </div>
      )}
    </section>
  );
};

export default function BrokenTileSalesList() {
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;

  const [allData, setAllData] = useState([]);
  const [totalBrokenTiles, setTotalBrokenTiles] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(PERIOD_CUSTOM);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [draftFilters, setDraftFilters] = useState(() => ({ ...defaultFilterDates(), validation: '' }));
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [selectedItem, setSelectedItem] = useState(null);

  const analyticsDateParams = useMemo(() => ({
    fromDate: appliedFilters.dateFrom || undefined,
    toDate: appliedFilters.dateTo || undefined,
  }), [appliedFilters.dateFrom, appliedFilters.dateTo]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [salesRes, stockRes, analyticsRes] = await Promise.all([
        axios.get(`${APP_API_URL}/broken-tiles/sales`, { headers, params: analyticsDateParams }),
        axios.get(`${APP_API_URL}/broken-tiles`, { headers }),
        axios.get(`${APP_API_URL}/broken-tiles/sales/analytics`, {
          headers,
          params: analyticsDateParams,
        }),
      ]);
      setAllData(salesRes.data?.sales || []);
      setTotalBrokenTiles(stockRes.data?.total_m2_disponible || 0);
      setAnalytics(analyticsRes.data?.success ? analyticsRes.data : null);
    } catch (error) {
      console.error('Erreur lors de la récupération des ventes :', error);
      setAllData([]);
      setAnalytics(null);
    } finally {
      setIsLoading(false);
    }
  }, [analyticsDateParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const activeDateRange = useMemo(() => {
    if (appliedFilters.dateFrom || appliedFilters.dateTo) {
      const from = appliedFilters.dateFrom ? dateInputStart(appliedFilters.dateFrom) : null;
      const to = appliedFilters.dateTo ? dateInputEnd(appliedFilters.dateTo) : null;
      return { from, to };
    }
    return null;
  }, [appliedFilters]);

  const filteredItems = useMemo(() => {
    return allData.filter((item) => {
      const search = appliedFilters.search.trim().toLowerCase();
      if (search) {
        const haystack = [
          item.customer?.name,
          item.note,
          item.user?.name,
          getPaymentLabel(item.payment_method),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      if (activeDateRange && item.created_at) {
        if (!isDateInRange(item.created_at, activeDateRange.from, activeDateRange.to)) {
          return false;
        }
      }
      if (appliedFilters.validation === 'validated' && !isSaleValidated(item)) return false;
      if (appliedFilters.validation === 'pending' && isSaleValidated(item)) return false;
      return true;
    });
  }, [allData, appliedFilters, activeDateRange]);

  const sortedItems = useMemo(() => {
    const sorted = [...filteredItems];
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      const aValue = getSortValue(a, key);
      const bValue = getSortValue(b, key);
      if (key === 'total_amount' || key === 'discount' || key === 'items' || key === 'validated') {
        return (aValue - bValue) * dir;
      }
      if (key === 'created_at') {
        return (new Date(aValue) - new Date(bValue)) * dir;
      }
      const aStr = String(aValue);
      const bStr = String(bValue);
      if (aStr < bStr) return -1 * dir;
      if (aStr > bStr) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [filteredItems, sortConfig]);

  const stats = useMemo(() => {
    let validated = 0;
    let pending = 0;
    filteredItems.forEach((item) => {
      if (isSaleValidated(item)) validated += 1;
      else pending += 1;
    });
    return {
      total: filteredItems.length,
      validated,
      pending,
      revenueNet: filteredItems.reduce((acc, item) => acc + getSaleNetRevenue(item), 0),
      discountTotal: filteredItems.reduce((acc, item) => acc + (parseFloat(item.discount) || 0), 0),
    };
  }, [filteredItems]);

  const lossSummary = analytics?.summary ?? null;
  const byVariant = analytics?.by_variant ?? [];
  const salesMetrics = analytics?.sales_metrics ?? {};

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PER_PAGE));
  const indexOfLast = currentPage * PER_PAGE;
  const indexOfFirst = indexOfLast - PER_PAGE;
  const currentItems = sortedItems.slice(indexOfFirst, indexOfLast);

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) setCurrentPage((p) => p + 1);
    else if (direction === 'prev' && currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const isSortActive = (key) => sortConfig.key === key;

  const handlePeriodSelect = (option) => {
    const dates = datesForPeriod(option.value);
    setSelectedPeriod(option);
    setDraftFilters((prev) => ({ ...prev, ...dates }));
    setAppliedFilters((prev) => ({
      ...prev,
      period: '',
      ...dates,
    }));
    setCurrentPage(1);
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setSelectedPeriod(PERIOD_CUSTOM);
    setAppliedFilters((prev) => ({
      ...prev,
      period: '',
      dateFrom: draftFilters.dateFrom,
      dateTo: draftFilters.dateTo,
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setSelectedPeriod(PERIOD_CUSTOM);
    setDraftFilters({ ...defaultFilterDates(), validation: '' });
    setAppliedFilters({ ...DEFAULT_FILTERS });
    setCurrentPage(1);
    setSortConfig({ key: 'created_at', direction: 'desc' });
  };

  const handleRefresh = () => fetchData();

  const exportRows = (items) =>
    items.map((item) => ({
      Date: formatDate(item.created_at),
      Client: item.customer?.name ?? '—',
      Articles: normalizeItems(item.items).length,
      'Montant net encaissé': `${formatNumber(getSaleNetRevenue(item))} FCFA`,
      Remise: `${formatNumber(item.discount)} FCFA`,
      Validation: isSaleValidated(item) ? 'Validée' : 'Non validée',
      Paiement: getPaymentLabel(item.payment_method),
      Note: item.note || '—',
      'Ajouté par': item.user?.name ?? '—',
    }));

  const exportToPDF = () => {
    const rows = exportRows(filteredItems.length ? filteredItems : currentItems);
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.text('Ventes carreaux cassés', 14, 16);
    doc.autoTable({
      head: [['Date', 'Client', 'Articles', 'Montant net', 'Remise', 'Validation', 'Paiement', 'Note', 'Vendeur']],
      body: rows.map((r) => [
        r.Date,
        r.Client,
        r.Articles,
        r['Montant net encaissé'],
        r.Remise,
        r.Validation,
        r.Paiement,
        r.Note,
        r['Ajouté par'],
      ]),
      startY: 24,
      headStyles: { fillColor: [88, 204, 2] },
      styles: { fontSize: 8 },
    });
    doc.save(`Ventes Carreaux Cassés ${APP_NAME}.pdf`);
  };

  const exportToExcel = () => {
    const rows = exportRows(filteredItems.length ? filteredItems : currentItems);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ventes');
    XLSX.writeFile(wb, `Ventes Carreaux Cassés ${APP_NAME}.xlsx`);
  };

  const handlePrint = () => {
    const rows = exportRows(filteredItems.length ? filteredItems : currentItems);
    const printWindow = window.open('', '', 'height=800,width=1200');
    const tableRows = rows
      .map(
        (r) => `
      <tr>
        <td>${r.Date}</td><td>${r.Client}</td><td>${r.Articles}</td>
        <td>${r['Montant net encaissé']}</td><td>${r.Remise}</td><td>${r.Validation}</td><td>${r.Paiement}</td>
        <td>${r.Note}</td><td>${r['Ajouté par']}</td>
      </tr>`
      )
      .join('');
    printWindow.document.write(`
      <html><head><title>Ventes</title>
      <style>table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:6px;font-size:12px}</style>
      </head><body><h1>Ventes carreaux cassés</h1><table>
      <thead><tr><th>Date</th><th>Client</th><th>Articles</th><th>Montant net</th><th>Remise</th><th>Validation</th><th>Paiement</th><th>Note</th><th>Vendeur</th></tr></thead>
      <tbody>${tableRows}</tbody></table></body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  const detailItems = selectedItem ? normalizeItems(selectedItem.items) : [];
  const selectedMetrics = selectedItem ? salesMetrics[String(selectedItem.id)] : null;
  const subtotal =
    selectedItem &&
    (parseFloat(selectedItem.total_amount) || 0) + (parseFloat(selectedItem.discount) || 0);

  return (
    <MainLayout>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Carreaux cassés</p>
          <h1 className="duo-hero-title">Ventes carreaux cassés</h1>
          <p className="duo-hero-sub mt-2 mb-0">
            Historique des ventes, analyse perte (coût revient vs CA) et répartition estimée par carreau.
          </p>
        </div>

        <div
          className="duo-kpi-chip mb-3"
          style={{
            border: '2px solid #58cc02',
            background: 'linear-gradient(135deg, #f7fff0 0%, #fff 100%)',
            padding: '1rem 1.25rem',
          }}
        >
          <p className="duo-kpi-chip__label mb-1">
            Encaissements (période)
            <BrokenTileInfoHint text={TOOLTIP_ENCAISSEMENTS} placement="bottom" />
          </p>
          <p className="mb-0 fw-bold" style={{ fontSize: '1.75rem', color: '#3c3c3c', lineHeight: 1.2 }}>
            {formatNumber(stats.revenueNet)} <span style={{ fontSize: '1rem', fontWeight: 600 }}>XOF</span>
          </p>
          <p className="mb-0 mt-1 small text-muted">
            Montant net encaissé (après remise) — hors coût FIFO et hors perte
            {stats.discountTotal > 0 && (
              <> · remises : −{formatNumber(stats.discountTotal)} XOF</>
            )}
          </p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Ventes (filtre)</p>
            <p className="duo-kpi-chip__value">{stats.total}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Validées</p>
            <p className="duo-kpi-chip__value">{stats.validated}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Non validées</p>
            <p className="duo-kpi-chip__value">{stats.pending}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">
              m² cassés (période)
            </p>
            <p className="duo-kpi-chip__value">{formatNumber(lossSummary?.m2_broken ?? 0)}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">m² vendus (période)</p>
            <p className="duo-kpi-chip__value">{formatNumber(lossSummary?.m2_sold ?? 0)}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">
              Coût revient vendu (FIFO)
              <BrokenTileInfoHint text={TOOLTIP_COST_FIFO_PERIOD} placement="bottom" />
            </p>
            <p className="duo-kpi-chip__value" style={{ fontSize: '1rem' }}>
              {formatNumber(lossSummary?.cost_sold_fifo ?? 0)} XOF
            </p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">
              Perte nette (période)
              <BrokenTileInfoHint text={TOOLTIP_LOSS_PERIOD} placement="bottom" />
            </p>
            <p className="duo-kpi-chip__value" style={{ fontSize: '1rem' }}>
              <LossValue value={lossSummary?.loss_sold ?? 0} />
            </p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Stock dispo. m²</p>
            <p className="duo-kpi-chip__value">{formatNumber(totalBrokenTiles)}</p>
          </div>
        </div>

        {byVariant.length > 0 && (
          <section className="duo-panel mb-3">
            <div className="duo-toolbar">
              <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
                Répartition par carreau (période)
              </span>
            </div>
            <div className="duo-table-wrap duo-table-wrap--hint-visible">
              <table className="duo-table">
                <thead>
                  <tr>
                    <th>Carreau</th>
                    <th>m² cassés</th>
                    <th>Coût cassé</th>
                    <th>
                      m² vendus (FIFO)
                      <BrokenTileInfoHint text={TOOLTIP_M2_FIFO} placement="right" />
                    </th>
                    <th>
                      Coût vendu (FIFO)
                      <BrokenTileInfoHint text={TOOLTIP_COST_FIFO_PERIOD} placement="right" />
                    </th>
                    <th>CA alloué</th>
                    <th>
                      Perte
                      <BrokenTileInfoHint text={TOOLTIP_LOSS_PERIOD} placement="right" />
                    </th>
                    <th>m² restants</th>
                  </tr>
                </thead>
                <tbody>
                  {byVariant.map((row) => (
                    <tr key={row.variant_id}>
                      <td className="fw-bold">{row.product_name}</td>
                      <td>{formatNumber(row.m2_broken)}</td>
                      <td>{formatNumber(row.cost_broken_total)} XOF</td>
                      <td>{formatNumber(row.m2_sold_allocated)}</td>
                      <td>{formatNumber(row.cost_sold_allocated)} XOF</td>
                      <td>{formatNumber(row.revenue_allocated)} XOF</td>
                      <td>
                        <LossValue value={row.loss_allocated} />
                      </td>
                      <td>{formatNumber(row.m2_remaining ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <div className="mb-3">
          <DuoPeriodPicker
            options={periodOptions}
            selectedOption={selectedPeriod}
            onSelect={handlePeriodSelect}
          />
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Liste des ventes
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-pdf" />
              </button>
              <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-xls" />
              </button>
              <button type="button" title="Imprimer" onClick={handlePrint} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-printer" />
              </button>
              <button type="button" title="Rafraîchir" onClick={handleRefresh} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${filtersOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${filtersOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
              {!isRole13 && (
                <Link to="/admin/sales-broken-tiles" className="duo-btn duo-btn--primary">
                  <i className="bi bi-cart-plus" /> Vendre
                </Link>
              )}
            </div>
          </div>

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher client, paiement, note..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <div className="d-flex flex-wrap gap-2 mt-2">
              <span className="small text-muted align-self-center me-1">Validation :</span>
              {[
                { value: '', label: 'Toutes' },
                { value: 'validated', label: 'Validées' },
                { value: 'pending', label: 'Non validées' },
              ].map(({ value, label }) => (
                <button
                  key={value || 'all'}
                  type="button"
                  className={`duo-btn duo-btn--sm${
                    appliedFilters.validation === value ? ' duo-btn--primary' : ' duo-btn--ghost'
                  }`}
                  onClick={() => {
                    setAppliedFilters((prev) => ({ ...prev, validation: value }));
                    setDraftFilters((prev) => ({ ...prev, validation: value }));
                    setCurrentPage(1);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {filtersOpen && (
            <div className="duo-filters">
              <form onSubmit={handleApplyFilters}>
                <p className="small text-muted mb-2 mb-md-0">
                  La plage de dates est celle qui est recherchée. Une période rapide remplit ces dates.
                </p>
                <div className="duo-filters__grid">
                  <div>
                    <label className="duo-form-label">Date de début</label>
                    <input
                      type="date"
                      className="duo-input"
                      value={draftFilters.dateFrom}
                      onChange={(e) => {
                        setSelectedPeriod(PERIOD_CUSTOM);
                        setDraftFilters((prev) => ({ ...prev, dateFrom: e.target.value }));
                      }}
                    />
                  </div>
                  <div>
                    <label className="duo-form-label">Date de fin</label>
                    <input
                      type="date"
                      className="duo-input"
                      value={draftFilters.dateTo}
                      onChange={(e) => {
                        setSelectedPeriod(PERIOD_CUSTOM);
                        setDraftFilters((prev) => ({ ...prev, dateTo: e.target.value }));
                      }}
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Appliquer les filtres
                  </button>
                </div>
              </form>
            </div>
          )}

          {(appliedFilters.dateFrom || appliedFilters.dateTo) && (
            <div className="px-3 py-2 border-bottom small text-muted" style={{ borderColor: '#f0f0f0' }}>
              <>
                Plage : {appliedFilters.dateFrom || '…'} → {appliedFilters.dateTo || '…'}
                {selectedPeriod.label ? ` (${selectedPeriod.label})` : ''}
              </>
            </div>
          )}

          <div className="duo-table-wrap duo-table-wrap--hint-visible">
            <table className="duo-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('created_at')}>
                    Date <SortIcon active={isSortActive('created_at')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('customer')}>
                    Client <SortIcon active={isSortActive('customer')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('items')}>
                    Articles <SortIcon active={isSortActive('items')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('total_amount')}>
                    Encaissé <SortIcon active={isSortActive('total_amount')} direction={sortConfig.direction} />
                    <BrokenTileInfoHint text={TOOLTIP_ENCAISSEMENTS} placement="right" />
                  </th>
                  <th>
                    m² vendus
                    <BrokenTileInfoHint text={TOOLTIP_M2_FIFO} placement="right" />
                  </th>
                  <th>
                    Coût FIFO
                    <BrokenTileInfoHint text={TOOLTIP_COST_FIFO_SALE} placement="right" />
                  </th>
                  <th>
                    Perte
                    <BrokenTileInfoHint text={TOOLTIP_LOSS} placement="right" />
                  </th>
                  <th onClick={() => requestSort('payment_method')}>
                    Paiement <SortIcon active={isSortActive('payment_method')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('user')}>
                    Vendeur <SortIcon active={isSortActive('user')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('validated')}>
                    Validation{' '}
                    <SortIcon active={isSortActive('validated')} direction={sortConfig.direction} />
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={11} className="duo-empty-row">
                      Chargement des ventes…
                    </td>
                  </tr>
                ) : currentItems.length > 0 ? (
                  currentItems.map((item) => {
                    const metrics = salesMetrics[String(item.id)];
                    return (
                    <tr key={item.id}>
                      <td className="text-nowrap">{formatDate(item.created_at)}</td>
                      <td className="fw-bold">{item.customer?.name ?? '—'}</td>
                      <td>
                        <span className="duo-badge-pill duo-badge-pill--info">
                          {normalizeItems(item.items).length}
                        </span>
                      </td>
                      <td>{formatNumber(getSaleNetRevenue(item))} XOF</td>
                      <td>{formatNumber(metrics?.m2_sold ?? '—')}</td>
                      <td>
                        {metrics ? (
                          <>
                            {formatNumber(metrics.cost_fifo)} XOF
                            <BrokenTileInfoHint text={TOOLTIP_COST_FIFO_SALE} placement="right" />
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {metrics ? (
                          <>
                            <LossValue value={metrics.loss} />
                            <BrokenTileInfoHint text={TOOLTIP_LOSS} placement="right" />
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{getPaymentLabel(item.payment_method)}</td>
                      <td>{item.user?.name ?? '—'}</td>
                      <td>
                        <ValidationBadge validated={isSaleValidated(item)} />
                      </td>
                      <td>
                        <div className="d-flex gap-2 flex-wrap">
                          <button
                            type="button"
                            className="duo-btn duo-btn--ghost duo-btn--sm"
                            onClick={() => setSelectedItem(item)}
                          >
                            Détails
                          </button>
                          <Link
                            to={`/broken-tiles/validate/${item.id}`}
                            className="duo-btn duo-btn--primary duo-btn--sm"
                          >
                            {isSaleValidated(item) ? 'Voir facture' : 'Valider'}
                          </Link>
                        </div>
                      </td>
                    </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={11} className="duo-empty-row">
                      Aucune vente correspondant aux critères.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {currentItems.length > 0 && (
            <div className="duo-footer-bar">
              <span>
                {indexOfFirst + 1} – {Math.min(indexOfLast, sortedItems.length)} sur {sortedItems.length}{' '}
                entrée{sortedItems.length > 1 ? 's' : ''}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={isLoading || currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <span className="duo-pagination__info px-2">
                    Page {currentPage} / {totalPages}
                  </span>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={isLoading || currentPage >= totalPages}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>

        <CalculationNotePanel />
      </div>

      {selectedItem && (
        <DuoModal
          size="lg"
          title={`Facture — ${selectedItem.customer?.name ?? 'Client'}`}
          onClose={() => setSelectedItem(null)}
          footer={
            <button type="button" className="duo-btn duo-btn--ghost" onClick={() => setSelectedItem(null)}>
              Fermer
            </button>
          }
        >
          <div className="row g-3 mb-3 small">
            <div className="col-md-6">
              <p className="mb-1 text-muted">Client</p>
              <p className="fw-bold mb-0">{selectedItem.customer?.name ?? '—'}</p>
            </div>
            <div className="col-md-6 text-md-end">
              <p className="mb-1 text-muted">Date</p>
              <p className="mb-0">{formatDate(selectedItem.created_at)}</p>
              <p className="mb-1 text-muted mt-2">Validation</p>
              <p className="mb-2">
                <ValidationBadge validated={isSaleValidated(selectedItem)} />
              </p>
              <p className="mb-1 text-muted">Vendeur</p>
              <p className="mb-0">{selectedItem.user?.name ?? '—'}</p>
            </div>
          </div>

          <div className="duo-table-wrap mb-3">
            <table className="duo-table">
              <thead>
                <tr>
                  <th>Format (m²)</th>
                  <th>Sacs</th>
                  <th>Quantité (m²)</th>
                  <th>Prix / m²</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {detailItems.length > 0 ? (
                  detailItems.map((it, idx) => (
                    <tr key={idx}>
                      <td>{it.format ?? it.bag_size ?? '—'}</td>
                      <td>{it.quantity}</td>
                      <td>{formatNumber(itemM2(it))}</td>
                      <td>{formatNumber(it.unit_price)} XOF</td>
                      <td>{formatNumber(it.total_price)} XOF</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="duo-empty-row">
                      Aucun article
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {selectedMetrics?.allocations?.length > 0 && (
            <>
              <p className="small fw-bold mb-2">
                Répartition FIFO estimée par carreau
                <BrokenTileInfoHint text={TOOLTIP_COST_FIFO_SALE} placement="bottom" />
              </p>
              <div className="duo-table-wrap duo-table-wrap--hint-visible mb-3">
                <table className="duo-table">
                  <thead>
                    <tr>
                      <th>Carreau</th>
                      <th>m²</th>
                      <th>
                        Coût / m²
                        <BrokenTileInfoHint text={TOOLTIP_COST_FIFO_PERIOD} placement="right" />
                      </th>
                      <th>
                        Coût total
                        <BrokenTileInfoHint text={TOOLTIP_COST_FIFO_SALE} placement="right" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedMetrics.allocations.map((alloc, idx) => (
                      <tr key={`${alloc.variant_id}-${idx}`}>
                        <td>{alloc.product_name}</td>
                        <td>{formatNumber(alloc.m2)}</td>
                        <td>{formatNumber(alloc.cost_per_m2)} XOF</td>
                        <td>{formatNumber(alloc.cost_total)} XOF</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="row g-2 mb-3 small">
                <div className="col-md-4">
                  <span className="text-muted">m² vendus :</span>{' '}
                  <strong>{formatNumber(selectedMetrics.m2_sold)}</strong>
                </div>
                <div className="col-md-4">
                  <span className="text-muted">
                    Coût FIFO
                    <BrokenTileInfoHint text={TOOLTIP_COST_FIFO_SALE} placement="bottom" /> :
                  </span>{' '}
                  <strong>{formatNumber(selectedMetrics.cost_fifo)} XOF</strong>
                </div>
                <div className="col-md-4">
                  <span className="text-muted">
                    Perte
                    <BrokenTileInfoHint text={TOOLTIP_LOSS} placement="bottom" /> :
                  </span>{' '}
                  <strong>
                    <LossValue value={selectedMetrics.loss} />
                  </strong>
                </div>
              </div>
            </>
          )}

          <div className="text-end" style={{ fontSize: '0.9rem' }}>
            <p className="mb-1">
              <span className="text-muted">Sous-total :</span> {formatNumber(subtotal)} XOF
            </p>
            <p className="mb-1">
              <span className="text-muted">Remise :</span> {formatNumber(selectedItem.discount)} XOF
            </p>
            <p className="fw-bold mb-2" style={{ fontSize: '1.05rem' }}>
              Net encaissé : {formatNumber(getSaleNetRevenue(selectedItem))} XOF
            </p>
            <p className="mb-1">
              <span className="text-muted">Paiement :</span> {getPaymentLabel(selectedItem.payment_method)}
            </p>
            {selectedItem.note && (
              <p className="mb-0">
                <span className="text-muted">Note :</span> {selectedItem.note}
              </p>
            )}
          </div>
        </DuoModal>
      )}
    </MainLayout>
  );
}
